'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { VideoProvider } from '@shared/utils/video';

interface Props {
  provider: VideoProvider;
  videoId: string;
  posterUrl: string | null;
  /** Base embed URL (no autoplay) — prefetched on hover for iframe providers. */
  embedUrl: string;
  /** Autoplay-with-sound URL — loaded in the modal on click (iframe providers). */
  autoplayUrl: string;
  preconnectOrigins: string[];
  preloadOnHover: boolean;
  /** CSS vars for responsive aspect-ratio + radius (consumed by .sp-video-aspect). */
  aspectStyle: React.CSSProperties;
  /** Player aspect ratio inside the modal, e.g. '16 / 9' or '9 / 16'. */
  modalAspect: string;
  vertical: boolean;
  scrim: boolean;
  playIconColor: string;
  playFillColor: string;
}

function PlayGlyph({ color, size }: { color: string; size: number }) {
  // Stroke with round joins/caps gives the triangle curved corners.
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color} stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" style={{ marginLeft: size * 0.08 }}>
      <polygon points="7 5 19 12 7 19" />
    </svg>
  );
}

export function VideoPopup({
  provider,
  videoId,
  posterUrl,
  embedUrl,
  autoplayUrl,
  preconnectOrigins,
  preloadOnHover,
  aspectStyle,
  modalAspect,
  vertical,
  scrim,
  playIconColor,
  playFillColor,
}: Props) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const warmedRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const wistiaRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLImageElement>(null);
  const [thumbLoaded, setThumbLoaded] = useState(false);

  const isWistia = provider === 'wistia';
  // Wistia loads its own player runtime; iframe providers prefetch the embed doc.
  const prefetchUrl = isWistia ? 'https://fast.wistia.com/player.js' : embedUrl;

  useEffect(() => setMounted(true), []);

  // Reveal the poster if it already loaded before hydration (e.g. an
  // above-the-fold popup whose lazy img resolved before React attached onLoad).
  useEffect(() => {
    if (thumbRef.current?.complete) setThumbLoaded(true);
  }, []);

  // Warm the player connection on first hover/focus so click-to-open is fast,
  // without executing any player code until the visitor actually clicks.
  const warm = useCallback(() => {
    if (warmedRef.current || !preloadOnHover) return;
    warmedRef.current = true;
    for (const origin of preconnectOrigins) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      document.head.appendChild(link);
    }
    const prefetch = document.createElement('link');
    prefetch.rel = 'prefetch';
    prefetch.as = isWistia ? 'script' : 'document';
    prefetch.href = prefetchUrl;
    document.head.appendChild(prefetch);
  }, [preconnectOrigins, preloadOnHover, prefetchUrl, isWistia]);

  // ESC to close + body scroll lock + focus management while the modal is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      triggerRef.current?.focus();
    };
  }, [open]);

  // Wistia: mount the Aurora <wistia-player autoplay> web component. This plays
  // with sound (browser-permitting) where the bare iframe falls back to muted,
  // and it's Wistia's documented autoplay-with-sound path. Player runtime loads
  // only now, on open. videoId is provider-validated ([\w-]) so it's safe to
  // interpolate. Clearing innerHTML on close destroys the player → stops audio.
  useEffect(() => {
    if (!open || !isWistia || !wistiaRef.current) return;
    if (!document.querySelector('script[data-wistia-player]')) {
      const s = document.createElement('script');
      s.src = 'https://fast.wistia.com/player.js';
      s.async = true;
      s.setAttribute('data-wistia-player', '');
      document.head.appendChild(s);
    }
    const container = wistiaRef.current;
    container.innerHTML = `<wistia-player media-id="${videoId}" autoplay style="display:block;width:100%;height:100%"></wistia-player>`;

    // The Aurora player defaults to muted on autoplay (a safe fallback) and can
    // re-mute itself a moment later when audio actually starts. The visitor's
    // click grants the activation that permits sound, so we re-assert
    // muted=false for the first few seconds after the element upgrades (after
    // player.js loads, async), then stop so we never fight a manual mute.
    // Whether sound is ultimately allowed is the browser's call (same for every
    // provider) — this maximizes the chance it plays with audio on click.
    let tries = 0;
    const iv = window.setInterval(() => {
      tries += 1;
      const el = container.querySelector('wistia-player') as (HTMLElement & { muted?: boolean; play?: () => void }) | null;
      if (el) {
        try {
          el.muted = false;
          el.play?.();
        } catch { /* element not upgraded yet */ }
      }
      if (tries >= 16) window.clearInterval(iv); // re-assert for ~3.2s
    }, 200);

    return () => { window.clearInterval(iv); container.innerHTML = ''; };
  }, [open, isWistia, videoId]);

  // Iframe providers (YouTube/Vimeo): a freshly-injected cross-origin iframe
  // tends to autoplay muted, so once it loads we postMessage an unmute + play.
  // Retried a couple times because the player's API may not be ready on load.
  // Harmless no-op for Loom / any player that ignores the message.
  const pokeUnmute = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    if (provider === 'youtube') {
      win.postMessage('{"event":"command","func":"unMute","args":""}', '*');
      win.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (provider === 'vimeo') {
      win.postMessage('{"method":"setMuted","value":false}', '*');
      win.postMessage('{"method":"setVolume","value":1}', '*');
      win.postMessage('{"method":"play"}', '*');
    }
  }, [provider]);

  const onIframeLoad = useCallback(() => {
    pokeUnmute();
    setTimeout(pokeUnmute, 400);
    setTimeout(pokeUnmute, 1000);
  }, [pokeUnmute]);

  // Pass the author's raw colour through; the .sp-vpop-fill CSS applies
  // transparency via color-mix (works for hex or rgba alike).
  const fillVars = { '--sp-vpop-color': playFillColor || '#000000' };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={warm}
        onFocus={warm}
        onClick={() => setOpen(true)}
        aria-label="Play video"
        aria-haspopup="dialog"
        className="sp-video-aspect sp-vpop-trigger"
        style={{ ...aspectStyle, ...fillVars, border: 'none', padding: 0, cursor: 'pointer', background: '#000', display: 'block' } as React.CSSProperties}
      >
        {posterUrl && (
          <img
            ref={thumbRef}
            src={posterUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onLoad={() => setThumbLoaded(true)}
            className={`sp-thumb-fade${thumbLoaded ? ' sp-thumb-fade-in' : ''}`}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {scrim && (
          <span
            aria-hidden
            style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 100%)' }}
          />
        )}
        <span aria-hidden className="sp-vpop-fill" />
        <span aria-hidden className="sp-vpop-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PlayGlyph color={playIconColor} size={34} />
        </span>
      </button>

      {mounted && open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.8)',
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              aspectRatio: modalAspect,
              width: vertical ? 'auto' : 'min(92vw, 1100px)',
              height: vertical ? 'min(85vh, 860px)' : 'auto',
              maxWidth: '92vw',
              maxHeight: '85vh',
            }}
          >
            {isWistia ? (
              <div ref={wistiaRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 8, overflow: 'hidden' }} />
            ) : (
              <iframe
                ref={iframeRef}
                src={autoplayUrl}
                title="Video"
                onLoad={onIframeLoad}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
              />
            )}
            <button
              ref={closeRef}
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close video"
              style={{
                position: 'absolute',
                top: -40,
                right: 0,
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                padding: 4,
                lineHeight: 0,
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
